import {AxisConfiguration, InMemoryAxisDataService} from "@o2d/datasource";
import {DateTime} from "luxon";
import {BackendAxisDataService} from "../../services/backend-axis-data.service";
import type {TimelineViewConfig} from "../_shared/timeline-view.config";

/**
 * Creates axis configurations for the resource-planning timeline view.
 *
 * Why a dedicated factory instead of the shared `createTimelineAxisConfigurations`?
 * Resource-Planning intentionally has NO calendar aggregation row
 * (`row-section-calendar` / axisId `C4`). The shared factory always registers
 * that calendar axis — which, combined with the resource-planning layout that
 * lacks a corresponding area configuration, caused runtime crashes in
 * `@o2d/timeline` (undefined `.map()` on missing area configs).
 *
 * This factory mirrors the shared factory but omits the `C4` calendar axis.
 * Keep in sync with `_shared/timeline-axis.factory.ts` for axes A, B, C0–C3, D.
 *
 * Unlike the shared factory, the X-axis is anchored at `today` and only spans
 * forward — scrolling into the past is disabled by range.
 *
 * @param config - Entity-specific configuration for the Y-axis
 */
export const createResourcePlanningAxisConfigurations = (
    config: TimelineViewConfig
): AxisConfiguration<string>[] => [
    // X-axis: Fixed columns (column-section-0, 1, 2)
    {
        axis: 'x',
        axisId: 'A',
        sectionIds: ['column-section-0', 'column-section-1', 'column-section-2'],
        scrollable: false,
        dataService: {
            dataServiceId: 'dummy-x-axis',
            type: InMemoryAxisDataService,
            debug: false,
            options: {
                generator: () => [0, 1, 2]
            }
        }
    },
    // X-axis: DateTime timeline (column-section-3).
    // Axis starts at today (NOT at cursorX) because resource planning is
    // forward-looking — scrolling before today is disabled by clipping the
    // axis range to [today, today+730]. cursorX stays within that range due
    // to the clamp in the view's connectedCallback.
    {
        axis: 'x',
        axisId: 'B',
        sectionIds: ['column-section-3'],
        scrollable: true,
        dataService: {
            dataServiceId: 'datetime-axis',
            type: InMemoryAxisDataService,
            debug: false,
            options: {
                generator: () => {
                    const today = DateTime.utc().startOf('day');
                    const values: string[] = [];
                    for (let i = 0; i <= 730; i++) {
                        values.push(today.plus({days: i}).toISODate({precision: `day`})!);
                    }
                    return values;
                }
            }
        }
    },
    // Y-axis: Month/Year header (row-section-0)
    {
        axis: 'y',
        axisId: 'C0',
        sectionIds: ['row-section-0'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-0',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Calendar week header (row-section-1)
    {
        axis: 'y',
        axisId: 'C1',
        sectionIds: ['row-section-1'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-1',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Weekday header (row-section-2)
    {
        axis: 'y',
        axisId: 'C2',
        sectionIds: ['row-section-2'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-2',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // Y-axis: Day of month header (row-section-3)
    {
        axis: 'y',
        axisId: 'C3',
        sectionIds: ['row-section-3'],
        scrollable: false,
        dataService: {
            dataServiceId: 'header-y-axis-3',
            type: InMemoryAxisDataService,
            debug: false,
            options: { generator: () => [0] }
        }
    },
    // NOTE: No C4 calendar row here — resource-planning does not aggregate by calendar.
    // Y-axis: Entity rows (row-section-4) - backend-based
    {
        axis: 'y',
        axisId: 'D',
        sectionIds: ['row-section-4'],
        scrollable: true,
        dataService: {
            dataServiceId: `${config.entityType.toLowerCase()}-axis`,
            type: BackendAxisDataService,
            debug: false,
            // Cast required: AxisDataServiceConfiguration defaults options to Record<string, unknown>
            // but BackendAxisDataServiceOptions intentionally has no index signature for type safety.
            options: {
                entityType: config.entityType,
                sort: config.sort,
                onFirstFetch: config.onFirstFetch,
                ...(config.queryParams?.length ? { queryParams: config.queryParams } : {})
            } as unknown as Record<string, unknown>
        }
    }
];
